import { useState, useMemo, useEffect, useRef } from 'react'

interface Trade {
  ticker: string
  status: string
  closeAction: string | null
  closeDate: string | null
  expDate?: string | null
  buyDate?: string | null
  netPosition: number
  total?: number
  shareAppreciation?: number
}

interface PremiumByTickerModalProps {
  trades: Trade[]
  positions?: Array<{ ticker: string; status: string; costBasis: number; soldPrice?: number; soldDate?: string; sharesSold?: number }>
  onClose: () => void
}

type SortCol = 'totalIncome' | 'netPremium' | 'sharePnL'

function formatCurrency(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function PremiumByTickerModal({ trades, positions = [], onClose }: PremiumByTickerModalProps) {
  // Default to the current year so it auto-rolls forward (2027 shows 2027, no manual change).
  const [yearFilter, setYearFilter] = useState<string>(String(new Date().getFullYear()))
  const [sortCol, setSortCol] = useState<SortCol>('totalIncome')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  // Drag-to-move: offset applied to the modal relative to its centered position.
  // Resets to {0,0} (centered) every time the modal is opened.
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const dragState = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null)

  const handleDragStart = (e: React.MouseEvent) => {
    e.preventDefault()
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: dragOffset.x,
      originY: dragOffset.y,
    }
  }

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!dragState.current) return
      const { startX, startY, originX, originY } = dragState.current
      setDragOffset({
        x: originX + (e.clientX - startX),
        y: originY + (e.clientY - startY),
      })
    }
    const handleUp = () => { dragState.current = null }
    window.addEventListener('mousemove', handleMove)
    window.addEventListener('mouseup', handleUp)
    return () => {
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('mouseup', handleUp)
    }
  }, [])

  const handleSort = (col: SortCol) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortCol(col)
      setSortDir('asc')
    }
  }

  const sortIndicator = (col: SortCol) => sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''

  // Get available years from close dates (with fallback to expDate/buyDate)
  const years = useMemo(() => {
    const yrs = new Set<string>()
    trades.forEach(t => {
      const date = t.closeDate || t.expDate || t.buyDate
      if (date) {
        const yr = date.substring(0, 4)
        if (yr && yr !== '0000') yrs.add(yr)
      }
    })
    return Array.from(yrs).sort().reverse()
  }, [trades])

  // Filter and aggregate — include all CLOSED trades (including assigned)
  const tickerData = useMemo(() => {
    const eligible = trades.filter(t => {
      if (t.status !== 'closed') return false
      if (yearFilter === 'all') return true
      const date = t.closeDate || t.expDate || t.buyDate
      return date ? date.startsWith(yearFilter) : true
    })

    // Group by ticker
    const grouped: Record<string, { trades: number; netPremium: number; sharePnL: number }> = {}
    eligible.forEach(t => {
      if (!grouped[t.ticker]) grouped[t.ticker] = { trades: 0, netPremium: 0, sharePnL: 0 }
      grouped[t.ticker].trades++

      // For "Sold" trades, separate premium from share appreciation
      // Share P&L = netPosition - total (the appreciation from selling assigned shares)
      if (t.closeAction === 'Sold' && t.total != null) {
        const sharePnL = (t.netPosition || 0) - (t.total || 0)
        grouped[t.ticker].netPremium += t.total || 0
        grouped[t.ticker].sharePnL += sharePnL
      } else {
        grouped[t.ticker].netPremium += t.netPosition || 0
      }
    })

    // Include position sale appreciation (from positions closed via Sell button)
    const soldPositionsForYear = positions.filter(p => {
      if (p.status !== 'closed' || !p.soldPrice || !p.soldDate) return false
      if (yearFilter === 'all') return true
      return p.soldDate.startsWith(yearFilter)
    })
    soldPositionsForYear.forEach(p => {
      if (!grouped[p.ticker]) grouped[p.ticker] = { trades: 0, netPremium: 0, sharePnL: 0 }
      const shares = p.sharesSold || 0
      const appreciation = (p.soldPrice! - p.costBasis) * shares
      grouped[p.ticker].sharePnL += appreciation
      grouped[p.ticker].trades++
    })

    // Convert to sorted array
    const rows = Object.entries(grouped)
      .map(([ticker, data]) => ({
        ticker,
        trades: data.trades,
        netPremium: data.netPremium,
        sharePnL: data.sharePnL,
        totalIncome: data.netPremium + data.sharePnL,
      }))

    // Sort by selected column
    rows.sort((a, b) => {
      const diff = a[sortCol] - b[sortCol]
      return sortDir === 'asc' ? diff : -diff
    })

    return rows
  }, [trades, positions, yearFilter, sortCol, sortDir])

  const totals = useMemo(() => ({
    trades: tickerData.reduce((s, d) => s + d.trades, 0),
    netPremium: tickerData.reduce((s, d) => s + d.netPremium, 0),
    sharePnL: tickerData.reduce((s, d) => s + d.sharePnL, 0),
    totalIncome: tickerData.reduce((s, d) => s + d.totalIncome, 0),
  }), [tickerData])

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        style={{ transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header — drag handle */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b border-[var(--color-border)] cursor-move select-none"
          onMouseDown={handleDragStart}
        >
          <h2 className="text-lg font-bold text-[var(--color-text-primary)]">Premium by Ticker</h2>
          <div className="flex items-center gap-3" onMouseDown={e => e.stopPropagation()}>
            <select
              value={yearFilter}
              onChange={e => setYearFilter(e.target.value)}
              className="px-3 py-1.5 text-sm border border-[var(--color-border)] rounded-lg bg-white cursor-pointer"
            >
              <option value="all">All Time</option>
              {years.map(yr => <option key={yr} value={yr}>{yr}</option>)}
            </select>
            <button onClick={onClose} className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] text-xl cursor-pointer">✕</button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-y-auto flex-1 px-6 py-3">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-[var(--color-border)]">
                <th className="text-right py-2 pr-3 font-medium text-[var(--color-text-muted)] w-8">#</th>
                <th className="text-left py-2 font-medium text-[var(--color-text-muted)]">Ticker</th>
                <th className="text-right py-2 font-medium text-[var(--color-text-muted)]">Trades</th>
                <th
                  className="text-right py-2 font-medium text-[var(--color-text-muted)] cursor-pointer hover:text-[var(--color-accent)] select-none"
                  onClick={() => handleSort('netPremium')}
                >
                  Net Premium{sortIndicator('netPremium')}
                </th>
                <th
                  className="text-right py-2 font-medium text-[var(--color-text-muted)] cursor-pointer hover:text-[var(--color-accent)] select-none"
                  onClick={() => handleSort('sharePnL')}
                >
                  Share P&L{sortIndicator('sharePnL')}
                </th>
                <th
                  className="text-right py-2 font-medium text-[var(--color-text-muted)] cursor-pointer hover:text-[var(--color-accent)] select-none"
                  onClick={() => handleSort('totalIncome')}
                >
                  Total Income{sortIndicator('totalIncome')}
                </th>
              </tr>
            </thead>
            <tbody>
              {tickerData.map((row, index) => (
                <tr key={row.ticker} className="border-b border-[var(--color-border)]/50 hover:bg-[var(--color-bg-tertiary)]/50">
                  <td className="py-2 pr-3 text-right font-mono text-[var(--color-text-muted)]">{index + 1}</td>
                  <td className="py-2 font-medium text-[var(--color-accent)]">{row.ticker}</td>
                  <td className="py-2 text-right font-mono">{row.trades}</td>
                  <td className={`py-2 text-right font-mono ${row.netPremium >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>
                    ${formatCurrency(row.netPremium)}
                  </td>
                  <td className={`py-2 text-right font-mono ${row.sharePnL > 0 ? 'text-[var(--color-green)]' : row.sharePnL < 0 ? 'text-[var(--color-red)]' : 'text-[var(--color-text-muted)]'}`}>
                    {row.sharePnL !== 0 ? `$${formatCurrency(row.sharePnL)}` : '—'}
                  </td>
                  <td className={`py-2 text-right font-mono font-bold ${row.totalIncome >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>
                    ${formatCurrency(row.totalIncome)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer totals */}
        <div className="px-6 py-3 border-t border-[var(--color-border)] bg-[var(--color-bg-tertiary)]">
          <div className="flex justify-between text-sm font-bold">
            <span>Total ({totals.trades} trades)</span>
            <div className="flex gap-6">
              <span className={totals.netPremium >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}>
                ${formatCurrency(totals.netPremium)}
              </span>
              <span className={totals.sharePnL >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}>
                ${formatCurrency(totals.sharePnL)}
              </span>
              <span className={totals.totalIncome >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}>
                ${formatCurrency(totals.totalIncome)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
